/* eslint-disable react/no-danger */
import { memo, useState } from 'react';

import Svg from 'lib/svgs.json';
import { useInView } from 'react-intersection-observer';
import SiteAnnotation from 'components/site/site-annotation';
import { createGlobalStyle } from 'styled-components';
import { mqLargeMin } from 'styled/tools/media-queries';
import { createMarkup } from 'lib/utils/create-markup';

const tableClasses = ['c1', 'c2', 'c3', 'c4'];
const tableHead = [
  'Food',
  'Serving size',
  'Fiber/serving',
  'Quantity to match PDS-08®',
];

const tableContent = [
  [
    "<img loading=\"lazy\" src='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1649730999/PDS%20PDP/fiber/APPLE.png' alt='Apples' height='60px' width='60px' />Apples",
    '1 medium w/skin',
    '3.7g',
    '1.4 apples',
  ],
  [
    "<img loading=\"lazy\" src='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1649731000/PDS%20PDP/fiber/STRAWBERRY.png' alt='Strawberries' height='60px' width='60px' />Strawberries",
    '1 cup',
    '3.4g',
    '1.5 cups',
  ],
  [
    "<img loading=\"lazy\" src='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1649730999/PDS%20PDP/fiber/CHERRIES.png' alt='Cherries' height='60px' width='60px' />Cherries",
    '10 cherries',
    '1.3g',
    '38 cherries',
  ],
  [
    "<img loading=\"lazy\" src='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1649731018/PDS%20PDP/fiber/BANANA.png' alt='Bananas' height='60px' width='60px' />Bananas",
    '1 medium',
    '3.0g',
    '1.7 bananas',
  ],
  [
    "<img loading=\"lazy\" src='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1649731000/PDS%20PDP/fiber/BROC.png' alt='Broccoli' height='60px' width='60px' />Broccoli",
    '1/2 cup',
    '2.3g',
    '1.1 cups',
  ],
  [
    "<img loading=\"lazy\" src='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1649730999/PDS%20PDP/fiber/GARLIC.png' alt='Garlic' height='60px' width='60px' />Garlic",
    '1 clove',
    '0.19g',
    '26.3 cloves',
  ],
  [
    "<img loading=\"lazy\" src='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1649731000/PDS%20PDP/fiber/SWEETPOTATO.png' alt='Sweet Potatoes' height='60px' width='60px' />Sweet Potatoes",
    '1 medium',
    '3.0g',
    '1.7 sweet potatoes',
  ],
  [
    "<img loading=\"lazy\" src='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1649730999/PDS%20PDP/fiber/ALMONDS.png' alt='Almonds' height='60px' width='60px' />Almonds",
    '1/4 cup',
    '3.2g',
    '0.4 cups',
  ],
  [
    "<img loading=\"lazy\" src='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1649730999/PDS%20PDP/fiber/BROWNRICE.png' alt='Brown Rice' height='60px' width='60px' />Brown Rice",
    '1 cup',
    '3.5g',
    '1.4 cups',
  ],
  [
    "<img loading=\"lazy\" src='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1649730999/PDS%20PDP/fiber/BREAD.png' alt='Multi-grain bread' height='60px' width='60px' />Multi-grain bread",
    '1 slice',
    '1.5g',
    '3.3 slices',
  ],
];

const annotationClasses = ['a1', 'a2'];
const annotationHead = ['Group', 'Grams of fiber/day'];
const annotationContent = [
  ['Children 1-3 years', '19 grams'],
  ['Children 4-8 years', '25 grams'],
  ['Boys 9-13 years', '31 grams'],
  ['Girls 9-13 years', '26 grams'],
];

const TableRow = ({ items, classes }) => (
  <tr>
    {items.map((item, i) => (
      <td
        key={`item-${i}`}
        className={classes[i]}
        dangerouslySetInnerHTML={createMarkup(item)}
      />
    ))}
  </tr>
);

const BridgeGapStyles = createGlobalStyle`
  .pds08-bridge-gap {
  > .container {
    display: flex;
    flex-direction: column;
    margin: 60px 0 30px;

    @media ${mqLargeMin} {
      flex-direction: row;
      margin: 80px 0;
    }
  }

  .bridge-text {
    @media ${mqLargeMin} {
      width: 300px;
      min-width: 300px;
      margin-right: 50px;
    }

    h2 {
      margin-bottom: 30px;

      span {
        display: block;
        font-size: 36px;
        font-weight: 300;
        color: var(--text-primary);
        line-height: 1.1;
      }

      @media ${mqLargeMin} {
        span {
          font-size: 50px;
        }
      }
    }

    p,
    > div {
      margin-top: 1em;
      color: var(--text-primary);

      @media ${mqLargeMin} {
        max-width: 340px;
      }
    }
  }

  .bridge-table {
    margin-top: 30px;
    flex-grow: 1;
    overflow: hidden;
    transition: max-height 500ms ease-in-out;

    @media ${mqLargeMin} {
      margin: 0;
      max-height: unset !important;
    }

    table {
      width: 100%;
      border-collapse: collapse;
    }

    td {
      color: var(--text-primary);
      font-size: 13px;
      white-space: nowrap;

      @media ${mqLargeMin} {
        font-size: 16px;
      }
    }

    thead {
      td {
        padding-bottom: 16px;
        font-weight: 500;
      }
    }

    tbody {
      tr {
        border-top: 1.5px solid var(--outline-neutral);
        border-bottom: 1.5px solid var(--outline-neutral);
      }

      td {
        padding: 8px 0;

        @media ${mqLargeMin} {
          padding: 12px 0;
        }

        &.c1 {
          display: flex;
          align-items: center;

          img {
            margin-right: 8px;
            height: 60px;
            width: 60px;
            border-radius: 50%;
            object-fit: cover;

            @media ${mqLargeMin} {
              margin-right: 20px;
            }
          }
        }
      }
    }

    .c2,
    .c3 {
      text-align: center;
      display: none;

      @media ${mqLargeMin} {
        display: table-cell;
      }
    }

    .c4 {
      text-align: right;
    }
  }

  .bridge-annotation {
    h5,
    .h5-style {
      font-weight: 500;
      font-size: 16px;
    }

    p {
      margin: 20px 0;
    }

    table {
      width: 100%;
      border-collapse: collapse;
    }

    td {
      font-size: 13px;
      white-space: nowrap;
    }

    thead {
      td {
        padding-bottom: 10px;
        font-weight: 500;
      }
    }

    tbody {
      tr {
        border-top: 1px solid var(--outline-neutral);
        border-bottom: 1px solid var(--outline-neutral);
      }

      td {
        padding: 8px 0;
      }
    }

    .a2 {
      text-align: right;
    }
  }

  .view-more {
    font-size: 13px;
    color: var(--text-primary);
    margin-top: 20px;
    cursor: pointer;

    &.expanded {
      display: none;
    }

    @media ${mqLargeMin} {
      display: none;
    }

    svg {
      margin-left: 15px;
      stroke-width: 2px;

      path {
        color: var(--text-primary);
      }
    }
  }
}

`;

const PDS08BridgeGap = () => {
  const [expanded, setExpanded] = useState(false);

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });

  return (
    <section className='pds08-bridge-gap' ref={ref}>
      <BridgeGapStyles />
      <div className='container'>
        <div className='bridge-text'>
          <h2>
            <span>Bridges the</span>
            <span>fiber gap.</span>
          </h2>
          <p>
            Fiber is one of the hardest nutrients to incorporate into a
            child&apos;s diet—it can also directly impact the health of the gut
            microbiome.
          </p>
          <div>
            95% of children and adults in the US do not reach their
            <SiteAnnotation
              className='bridge-annotation'
              title='daily recommended fiber intake.'
              visible={inView}
            >
              <h3 className='h5-style'>
                Recommended pediatric daily fiber intake
              </h3>
              <p>
                With 5g of prebiotic fiber in one dose, PDS-08® makes it easier
                for parents to meet their child’s recommended daily fiber
                intake.
              </p>
              <table>
                <thead>
                  <TableRow
                    items={annotationHead}
                    classes={annotationClasses}
                  />
                </thead>
                <tbody>
                  {annotationContent.map((tableRow, i) => (
                    <TableRow
                      key={`tableRow-${i}`}
                      items={tableRow}
                      classes={annotationClasses}
                    />
                  ))}
                </tbody>
              </table>
            </SiteAnnotation>
            With 5 grams of fiber per serving, PDS-08® is a meaningful
            complement to a healthy diet.
          </div>
        </div>
        <div
          className={`bridge-table ${expanded ? 'expanded' : ''}`}
          style={{ maxHeight: expanded ? tableContent.length * 78 + 35 : 425 }}
        >
          <table>
            <thead>
              <TableRow items={tableHead} classes={tableClasses} />
            </thead>
            <tbody>
              {tableContent.map((tableRow, i) => (
                <TableRow
                  key={`tableRow-${i}`}
                  items={tableRow}
                  classes={tableClasses}
                />
              ))}
            </tbody>
          </table>
        </div>
        <div
          className={`view-more ${expanded ? 'expanded' : ''}`}
          onClick={() => setExpanded(true)}
          onKeyDown={() => setExpanded(true)}
          role='presentation'
        >
          View all
          <i
            className='icon'
            dangerouslySetInnerHTML={createMarkup(Svg.arrow_down)}
          />
        </div>
      </div>
    </section>
  );
};

export default memo(PDS08BridgeGap);
